/**
 * Import an existing policy library for a customer: a spreadsheet of
 * policies and a zip of their files, mapped, checked, run, and if need be
 * rolled back. The engine has existed on the backend for a long time; this
 * is the first screen that drives it.
 *
 * One page, one job at a time. The step shown follows the job's status
 * (draft → validating → validated → running → completed / failed /
 * rolled_back), so a page reload lands where the job is.
 */

import { Fragment, useEffect, useId, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BackLink } from '../../shared/BackLink';
import { ImportOwners } from './ImportOwners';
import { call, isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import field from '../admin/Admin.module.css';
import { CODE_MAX, ID_MAX } from '../../types/fieldLimits';

type JobStatus = 'draft' | 'validating' | 'validated' | 'running' | 'completed' | 'failed' | 'rolled_back';

export interface JobSummary { id: string; status: JobStatus; csvFileName: string; zipFileName?: string; totalRows: number; progress: number; stats: Stats; createdAt: unknown; createdAtIso?: string | null }

/** The two questions asked of the list of previous imports. */
export type RowsFilter = 'any' | 'with' | 'without';
export type ImportedFilter = 'any' | 'some' | 'none';
/**
 * Attempts pile up: a spreadsheet that would not read, a run started and
 * abandoned. "Had rows" is whether a spreadsheet was read at all; "imported"
 * is whether any policy came of it. They differ: a sheet of 500 rows that was
 * checked and never run had rows and imported nothing.
 */
export function filterJobs<T extends Pick<JobSummary, 'totalRows' | 'stats'>>(jobs: readonly T[], rows: RowsFilter, imported: ImportedFilter): T[] {
  return jobs.filter((j) => {
    const hadRows = (j.totalRows ?? 0) > 0;
    const importedSome = (j.stats?.created ?? 0) > 0;
    if (rows === 'with' && !hadRows) return false;
    if (rows === 'without' && hadRows) return false;
    if (imported === 'some' && !importedSome) return false;
    if (imported === 'none' && importedSome) return false;
    return true;
  });
}
interface Stats {
  created: number; failed: number; skipped: number; filesMissing: number;
  filesStored?: number; filesRejected?: number;
  /** Split out of `skipped`: lines an earlier import had already brought in. */
  alreadyImported?: number;
  /** Lines held back because the version group they belong to could not be imported. */
  blocked?: number;
  /** Policies created with something the sheet said that could not be used. */
  withNotes?: number;
  /** Published policies whose acknowledgement requirements are still being finished. */
  ackPending?: number;
}
/** The spreadsheet's files against the zip's (functions/src/import/fileMatch.ts). */
export interface FileMatch {
  zipProvided: boolean; named: number; found: number; missingCount: number;
  missing: Array<{ line: number; title: string; fileName: string; closest?: string }>;
  nearMatchCount: number; unreferencedCount: number; unreferenced: string[]; wrongTypeCount: number; wrongType: string[]; looksLikeWrongZip: boolean;
}
interface Validation { owners?: { people: number; policies: number }; totalRows: number; validRows: number; errorCount: number; warningCount: number; missingFiles: string[]; duplicateLegacyIds: string[]; canProceed: boolean; unreferencedFileCount?: number; wrongTypeFileCount?: number; files?: FileMatch; filesAccepted?: boolean; aboveSupportedSize?: { rows: number; supported: number } | null }
interface ReportFile { fileName: string; base64: string }
interface Job {
  id: string; orgId: string; status: JobStatus;
  csvFile: { fileName: string; sizeBytes: number };
  zipFile?: { fileName: string; sizeBytes: number };
  mapping: Record<string, string | undefined>;
  options: Options;
  /** The organization's own document types, for the type when the sheet has none. */
  documentTypes?: { id: string; name: string }[];
  validation?: Validation;
  execution: { cursor: number; totalRows: number; progress: number };
  stats: Stats;
  error?: string;
  /** Present once the files are uploaded and confirmed. The status stays `draft` until validation, so THIS ends step 1. */
  upload?: { confirmed: boolean; headers: string[]; rowCount: number; suggestedMapping: Record<string, string | undefined>; sampleRows: unknown[] } | null;
  csvCheck?: { lineErrors: number; warnings: number; acceptedFindings: boolean } | null;
  report?: { imported: number | null; notImported: number | null; generatedAt: string | null } | null;
}
interface Options { createTaxonomyIfMissing: boolean; defaultStatus: 'draft' | 'published'; skipDuplicateLegacyIds: boolean; generatePolicyNumbers: boolean; defaultReviewCycleMonths: number; defaultDocumentTypeId?: string | null; autoGeneratePdfFromDocxIfMissing: boolean; maxFileSizeBytes: number; dryRun: boolean }
interface Analysis { headers: string[]; rowCount: number; suggestedMapping: Record<string, string | undefined>; sampleRows: string[][] }

/** The columns the engine understands, in the order a person thinks of them. */
const MAPPING_FIELDS: Array<{ key: string; label: string; required?: boolean; hint: string }> = [
  { key: 'title', label: 'Title', required: true, hint: 'The policy title.' },
  { key: 'policyNumber', label: 'Policy number', hint: 'Kept as is; generated when blank.' },
  { key: 'legacyId', label: 'Legacy id', hint: 'The old system’s id, for tracing and duplicate checks.' },
  { key: 'department', label: 'Department', hint: 'Matched to one of your departments by name; created if the option below is on. Never used as the category.' },
  { key: 'category', label: 'Category', hint: 'Matched to one of your categories by name; created if the option below is on. Never used as the department.' },
  { key: 'documentType', label: 'Document type', hint: 'Matched to one of your document types by name. Never created by an import: add it under Taxonomy first. Every policy needs a type: choose a column here, or a type under Options for lines without one.' },
  { key: 'description', label: 'Summary', hint: '' },
  { key: 'owner', label: 'Owner email', hint: 'An active member\u2019s address; they become the owner. Somebody who has not joined yet is listed at the next step, and their policies pass to them when they join.' },
  { key: 'effectiveDate', label: 'Effective date', hint: 'YYYY-MM-DD.' },
  { key: 'reviewDate', label: 'Next review date', hint: 'YYYY-MM-DD.' },
  { key: 'status', label: 'Status', hint: 'draft or published; the default below applies when blank.' },
  { key: 'pdfFileName', label: 'PDF file name', hint: 'As it appears in the zip.' },
  { key: 'docxFileName', label: 'Word file name', hint: 'As it appears in the zip.' },
  { key: 'version', label: 'Version', hint: 'Optional. Not in the sheet: every row is imported as its own policy at version 1. With a column, each policy keeps the version number its row gives, and rows sharing a policy number become one policy with its history; the highest is current.' },
  { key: 'requiresAcknowledgement', label: 'Requires acknowledgement', hint: 'true or false.' },
  { key: 'acknowledgementDueDays', label: 'Days to acknowledge', hint: '' },
  { key: 'content', label: 'Body text', hint: 'Markdown, when there is no file.' },
];

/** Which step of the page a job's status belongs to. Exported for its test. */
/**
 * Which step the page shows. A job stays `draft` from "Start an import"
 * until validation runs, so the status alone cannot tell step 1 from step 2:
 * reading only the status sent a person who had just uploaded their files
 * straight back to the upload form. `filesConfirmed` is what ends step 1.
 */
export function stepFor(status: JobStatus | undefined, filesConfirmed = false): 'upload' | 'map' | 'run' | 'done' {
  if (!status) return 'upload';
  if (status === 'draft') return filesConfirmed ? 'map' : 'upload';
  if (status === 'validating' || status === 'validated') return 'map';
  if (status === 'running') return 'run';
  return 'done';
}

/**
 * Where this import is. The page shows one step at a time, so without this
 * a heading that says "1." promises a "2." nobody can see.
 */
const STEPS: Array<{ label: string; at: JobStatus[] }> = [
  { label: 'Spreadsheet and files', at: ['draft'] },
  { label: 'Columns', at: ['validating'] },
  { label: 'What will happen', at: ['validated'] },
  { label: 'Import', at: ['running'] },
  { label: 'Report', at: ['completed', 'failed', 'rolled_back'] },
];
export function ImportSteps({ status, filesConfirmed = false }: { status: JobStatus; filesConfirmed?: boolean }) {
  const current = status === 'draft' && filesConfirmed ? 1 : STEPS.findIndex((x) => x.at.includes(status));
  return <StepList steps={STEPS.map((x) => x.label)} current={current} />;
}

/** The steps of an import with the current one marked; shared by the library and the attestation imports. */
export function StepList({ steps, current }: { steps: string[]; current: number }) {
  const STEPS = steps.map((label) => ({ label }));
  return (
    <ol aria-label="Steps of this import" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2) var(--sp-4)', listStyle: 'none', padding: 0, margin: '0 0 var(--sp-4)', fontSize: 'var(--fs-meta)' }}>
      {STEPS.map((step, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'next';
        return (
          <li key={step.label} aria-current={state === 'current' ? 'step' : undefined}
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: state === 'next' ? 'var(--muted)' : 'var(--ink)', fontWeight: state === 'current' ? 600 : 400 }}>
            <span aria-hidden="true" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 20, height: 20, borderRadius: 10, fontSize: 11, fontWeight: 600,
              background: state === 'current' ? 'var(--accent)' : state === 'done' ? 'var(--accent-wash)' : 'transparent', color: state === 'current' ? 'var(--on-accent)' : state === 'done' ? 'var(--accent-ink)' : 'var(--muted)', border: state === 'next' ? '1px solid var(--line)' : '1px solid transparent' }}>
              {state === 'done' ? '✓' : i + 1}
            </span>
            <span><span className="vh">Step {i + 1} of {STEPS.length}{state === 'done' ? ', done' : ''}: </span>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function errorText(e: unknown, fallback: string): string {
  return isCallFailure(e) ? e.message : e instanceof Error ? e.message : fallback;
}

/** Hand the browser a file the callable returned as bytes. */
export function saveFile(f: ReportFile, mime: string): void {
  const bytes = Uint8Array.from(atob(f.base64), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = f.fileName; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Hand the browser a text file, such as the example CSV. */
export function saveText(fileName: string, text: string, mime = 'text/csv;charset=utf-8'): void {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = fileName; a.rel = 'noopener';
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * What the import engine accepts (IMPORT_LIMITS in functions/src/import/types.ts;
 * ImportPage.limits.test.ts fails if the two disagree). Said on the page and
 * checked before anything is uploaded: a refusal after a ten-minute upload
 * is the worst time to learn a limit.
 */
export const IMPORT_MAX = { csvMb: 50, rows: 50_000, zipMb: 500, unpackedMb: 1024, fileMb: 25 } as const;
const MB = 1024 * 1024;
/** These steps read a zip of up to 500 MB or import a batch; the server allows them nine minutes. */
export const LONG = { timeoutMs: 540_000 };

/** Why a chosen file cannot be uploaded, or null. */
export function tooLarge(kind: 'csv' | 'zip', file: File | null): string | null {
  if (!file) return null;
  const limit = kind === 'csv' ? IMPORT_MAX.csvMb : IMPORT_MAX.zipMb;
  if (file.size <= limit * MB) return null;
  const size = (file.size / MB).toFixed(0);
  return kind === 'csv'
    ? `${file.name} is ${size} MB. A spreadsheet can be up to ${limit} MB; split it into several imports.`
    : `${file.name} is ${size} MB. A zip can be up to ${limit} MB; split the files across several imports, each with its own spreadsheet.`;
}

export const EXAMPLE_CSV_NAME = 'policy-import-example.csv';
/** How long "Copied" stays on the button before it reads "Copy CSV" again. */
export const COPIED_MS = 2500;

/** The signed address has expired (an hour); the caller fetches a fresh one and tries once more. */
export class UploadWindowClosed extends Error { constructor() { super('The upload address expired. Try again.'); } }

/** The browser never reached storage: offline, or storage does not take uploads from this address. */
export class UploadNotSent extends Error {
  constructor() {
    super('The file could not be sent: the connection to storage was refused or dropped. Check your connection and try again. If it happens again, tell StandingDocs, since it is ours to fix.');
  }
}

export async function putFile(url: string, file: File, contentType: string): Promise<void> {
  let res: Response;
  try {
    res = await fetch(url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  } catch {
    // fetch rejects with the bare words "Failed to fetch", which tell a person
    // nothing. 2026-09-20: the bucket took uploads from the console's address
    // only, and the owner met those three words on the customer app.
    throw new UploadNotSent();
  }
  // The upload address is good for an hour from "Start an import".
  if (res.status === 403) throw new UploadWindowClosed();
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
}

/**
 * Two homes. In the console a StandingDocs administrator opens it from an
 * organization's page, so the organization is in the URL and there is a way
 * back to it. In the customer app the organization's own System Admin opens
 * it from the sidebar for the organization they are signed in to (`home:
 * 'app'`): a top-level page, so no back arrow, and the links stay in the app.
 */
export function ImportPage({ orgId: orgIdProp, home = 'console' }: { orgId?: string; home?: 'console' | 'app' } = {}) {
  const routeParams = useParams<{ orgId: string }>();
  const orgId = orgIdProp ?? routeParams.orgId;
  const listPath = home === 'app' ? '/admin/import' : `/console/orgs/${orgId}/import`;
  const [params, setParams] = useSearchParams();
  const jobId = params.get('job');
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const jobs = useQuery({
    queryKey: ['import-jobs', orgId],
    enabled: Boolean(orgId) && !jobId,
    retry: false,
    // Always read again when the list is shown. The app keeps data "fresh"
    // for a minute, and the list is first read when the page opens, empty:
    // coming back from a quick import showed "None yet" for an import that
    // had just finished.
    staleTime: 0,
    refetchOnMount: 'always',
    queryFn: () => call<{ orgId: string }, { jobs: JobSummary[] }>('listImportJobs', { orgId: orgId! }),
  });
  const status = useQuery({
    queryKey: ['import-job', jobId],
    enabled: Boolean(jobId),
    retry: false,
    refetchInterval: (q) => (q.state.data?.job.status === 'running' || q.state.data?.job.status === 'validating' ? 2000 : false),
    queryFn: () => call<{ jobId: string; includeErrorSample: boolean; errorSampleLimit: number }, { job: Job; errorSample: Array<{ rowNumber: number; message?: string; error?: string }> }>('getImportJobStatus', { jobId: jobId!, includeErrorSample: true, errorSampleLimit: 10 }),
  });
  const [rowsFilter, setRowsFilter] = useState<RowsFilter>('any');
  const [importedFilter, setImportedFilter] = useState<ImportedFilter>('any');
  const shownJobs = useMemo(() => filterJobs(jobs.data?.jobs ?? [], rowsFilter, importedFilter), [jobs.data, rowsFilter, importedFilter]);
  const job = status.data?.job;
  const filesConfirmed = Boolean(job?.upload?.confirmed);
  const step = stepFor(job?.status, filesConfirmed);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['import-job', jobId] });

  const create = useMutation({
    mutationFn: () => call<{ orgId: string; name: string }, { jobId: string; uploadUrls: { csv: string; zip: string } }>('createImportJob', { orgId: orgId!, name: 'Policy library import' }),
    onSuccess: (r) => {
      setCreatedFor({ jobId: r.jobId, uploadUrls: r.uploadUrls });
      setParams({ job: r.jobId });
    },
    onError: (e) => setFailure(errorText(e, 'Could not start the import.')),
  });
  /*
   * WHAT BELONGS TO THE IMPORT CURRENTLY OPEN.
   *
   * Both of these were read during render from somewhere React does not track,
   * and neither was scoped to an import:
   *
   *   `uploadUrls` was a ref set when an import was created and never cleared,
   *   so creating import A and then opening import B handed B's upload step
   *   A's signed URLs — which point at A's storage paths. B's spreadsheet and
   *   zip would have been written into A's folder.
   *
   *   `analysis` was worse: a plain object at MODULE scope, outside the
   *   component. A module-level value lives for the browser TAB, surviving
   *   unmount and navigation, so the second import of a session was offered the
   *   first one's headers and suggested mapping.
   *
   * Each value is now stored WITH the job it belongs to and read only when that
   * job is the one open. No reset logic, and so no window in which the id has
   * not caught up with the value — an earlier attempt at this cleared the URLs
   * during exactly that gap, which the import tests caught.
   */
  const [createdFor, setCreatedFor] = useState<
    { jobId: string; uploadUrls: { csv: string; zip: string } } | null>(null);
  const uploadUrls = createdFor && createdFor.jobId === jobId ? createdFor.uploadUrls : null;

  const [analysedFor, setAnalysedFor] = useState<{ jobId: string; analysis: Analysis } | null>(null);
  const analysis = analysedFor && analysedFor.jobId === jobId ? analysedFor.analysis : null;

  if (!orgId) return null;

  // The two shells frame a page differently. The console pads everything it
  // shows, and its title sits in a grid that reserves a column for the back
  // arrow. The customer app leaves the frame to each page: a header band and
  // a padded body, as every other page there has. Rendering the console's
  // frame inside the app put the content flush against the sidebar.
  const inApp = home === 'app';
  const subtitle = (
    <>
      A spreadsheet of policies and a zip of their PDF or Word files{inApp ? <>, to bring an existing library in at once instead of uploading documents one by one</> : <>, for <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{orgId}</span></>}.
      Nothing is created until the run step, and a run can be rolled back.
    </>
  );
  const Body = inApp ? 'div' : Fragment;

  return (
    <>
      {/* Inside an import the arrow goes back to the start of Import library
          (the list and "Start an import"), straight there, not through
          history. At the start, the console's arrow leads to the organization;
          the app's start is a sidebar page and has nowhere to go back to. */}
      {inApp ? (
        <header className={page.header}>
          {jobId ? (
            <div className={page.titled}>
              <div className={page.titleLine}>
                <BackLink to={listPath} label="Back to the start of Import library" direct />
                <h1 className={page.title}>Import a policy library</h1>
              </div>
              <p className={page.subtitle}>{subtitle}</p>
            </div>
          ) : (
            <>
              <h1 className={page.title}>Import a policy library</h1>
              <p className={page.subtitle}>{subtitle}</p>
            </>
          )}
        </header>
      ) : (
        <div className={page.titled}>
          <div className={page.titleLine}>
            {jobId
              ? <BackLink to={listPath} label="Back to the start of Import library" direct />
              : <BackLink to={`/console/orgs/${orgId}`} label="Back to the organization" />}
            <h1 className={own.title}>Import a policy library</h1>
          </div>
          <p className={own.subtitle}>{subtitle}</p>
          {/* The app has a ? in its shell for this; the console has none. A
              new tab, so a spreadsheet already chosen here is not lost. */}
          <p className={page.itemMeta} style={{ margin: 'var(--sp-2) 0 0' }}>
            <Link to="/console/help/importing-a-library" target="_blank" rel="noreferrer">How importing works</Link> (opens in a new tab)
          </p>
        </div>
      )}

      <Body {...(inApp ? { className: page.body } : {})}>
      {failure && <div className={own.error} role="alert">{failure}</div>}
      {notice && <div className={`${page.notice} ${page.noticeInfo}`} role="status">{notice}</div>}

      {!jobId && (
        <>
          <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
            <h2 className={page.sectionTitle}>Start an import</h2>
            <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
              You will be asked for the spreadsheet (and optionally the zip), then to say which column is which, then shown what will happen before anything is created.
            </p>
            <button type="button" className={own.googleBtn} style={{ width: 'auto', padding: '9px 16px' }} disabled={create.isPending} onClick={() => { setFailure(null); create.mutate(); }}>
              {create.isPending ? 'Starting…' : 'Start an import'}
            </button>
          </section>
          <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
            <h2 className={page.sectionTitle}>Attestation history</h2>
            <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)', maxWidth: '82ch' }}>
              The record of who confirmed they had read which policy, and when, from the system you are leaving. Import it after the library, so each attestation can be tied to its policy.
              It is kept as what it is, a record from another system, including for staff who have since left.
            </p>
            <Link to={`${listPath}/attestations`} className={page.btn} style={{ textDecoration: 'none', display: 'inline-block' }}>Import attestation history</Link>
          </section>
          <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
            <h2 className={page.sectionTitle}>Previous imports</h2>
            {jobs.isLoading ? <Spinner label="Loading imports" /> : jobs.error ? (
              // Not "None yet": a failed read is not an empty history.
              <div className={`${page.notice} ${page.noticeDanger}`} role="alert">
                {errorText(jobs.error, 'The list of previous imports could not be loaded. Reload the page to try again.')}
              </div>
            ) : (jobs.data?.jobs.length ?? 0) === 0 ? (
              <p className={page.itemMeta}>
                No imports yet. Each one is listed here once started, with what it created, and can be rolled back from here.
              </p>
            ) : (
              <>
              <div style={{ display: 'flex', gap: 'var(--sp-4)', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 'var(--sp-3)' }}>
                <div>
                  <label className={form.label} htmlFor="imports-rows">Spreadsheet</label>
                  <select id="imports-rows" className={form.select} style={{ minWidth: 220 }} value={rowsFilter} onChange={(e) => setRowsFilter(e.target.value as RowsFilter)}>
                    <option value="any">Any</option>
                    <option value="with">Had rows</option>
                    <option value="without">Had no rows</option>
                  </select>
                </div>
                <div>
                  <label className={form.label} htmlFor="imports-imported">Result</label>
                  <select id="imports-imported" className={form.select} style={{ minWidth: 220 }} value={importedFilter} onChange={(e) => setImportedFilter(e.target.value as ImportedFilter)}>
                    <option value="any">Any</option>
                    <option value="some">Imported policies</option>
                    <option value="none">Imported nothing</option>
                  </select>
                </div>
                <p className={page.itemMeta} role="status" aria-live="polite" style={{ margin: '0 0 9px' }}>
                  <strong>{shownJobs.length}</strong> of {jobs.data!.jobs.length} {jobs.data!.jobs.length === 1 ? 'import' : 'imports'}
                  {(rowsFilter !== 'any' || importedFilter !== 'any') && <>{' · '}<button type="button" className={page.linkBtn} onClick={() => { setRowsFilter('any'); setImportedFilter('any'); }}>Show all</button></>}
                </p>
              </div>
              <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)' }}>
                Open an import to see it, continue it, download its report, or delete its record. Deleting a record never removes the policies it imported.
              </p>
              {shownJobs.length === 0 && <p className={page.state}>No import matches those two choices.</p>}
              <ul className={page.list} aria-label="Previous imports">
                {shownJobs.map((j) => (
                  <li key={j.id} className={page.listItem}>
                    <div>
                      <div className={page.itemName}>{j.csvFileName || 'Untitled'} <span className={`${own.pill} ${j.status === 'completed' ? own.pillActive : j.status === 'failed' ? own.pillOther : own.pillDraft}`} style={{ marginLeft: 8 }}>{j.status.replace('_', ' ')}</span></div>
                      <div className={page.itemMeta}>
                        {j.createdAtIso && <>{new Date(j.createdAtIso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} · </>}
                        {j.totalRows > 0 ? `${j.totalRows.toLocaleString('en-US')} ${j.totalRows === 1 ? 'row' : 'rows'}` : 'no rows read'} · {j.stats.created > 0 ? `${j.stats.created.toLocaleString('en-US')} imported` : 'nothing imported'}{j.stats.failed > 0 ? `, ${j.stats.failed} failed` : ''}{(j.stats.alreadyImported ?? 0) > 0 ? `, ${j.stats.alreadyImported} already imported` : ''}{j.stats.skipped > 0 ? `, ${j.stats.skipped} skipped` : ''}
                      </div>
                    </div>
                    <button type="button" className={page.btn} aria-label={`Open ${j.csvFileName || 'Untitled'}, ${j.id}`} onClick={() => setParams({ job: j.id })}>Open</button>
                  </li>
                ))}
              </ul>
              </>
            )}
          </section>
        </>
      )}

      {jobId && status.isLoading && <Spinner label="Loading the import" />}
      {jobId && status.error && (
        <PageState title="Could not load this import" tone="alert" action={{ to: listPath, label: 'Back to all imports' }}>
          {errorText(status.error, 'The import did not come back. Reload the page to try again.')}
        </PageState>
      )}
      {jobId && job && (
        <>
          <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
            Import <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{job.id}</span> · {job.status.replace('_', ' ')}
          </p>
          <ImportSteps status={job.status} filesConfirmed={filesConfirmed} />
          {step === 'upload' && <UploadStep job={job} uploadUrls={uploadUrls} onDone={(a) => { setAnalysis(a); void refresh(); }} onError={setFailure} />}
          {step === 'map' && <MapStep job={job} analysis={analysis ?? analysisFromJob(job)} onSaved={() => { setNotice(null); void refresh(); }} onError={setFailure} onRun={() => void refresh()} />}
          {step === 'run' && <RunStep job={job} onTick={() => void refresh()} onError={setFailure} />}
          {step === 'done' && <DoneStep job={job} errorSample={status.data?.errorSample ?? []} onChanged={() => void refresh()} onError={setFailure} onNotice={setNotice} />}
          {job.status !== 'running' && (
            <DeleteImportRecord job={job} onError={setFailure} onDeleted={(kept) => {
              setParams({});
              void queryClient.invalidateQueries({ queryKey: ['import-jobs', orgId] });
              setNotice(kept > 0
                ? `The record of that import was deleted. The ${kept.toLocaleString('en-US')} ${kept === 1 ? 'policy' : 'policies'} it imported ${kept === 1 ? 'is' : 'are'} still in the library.`
                : 'The record of that import was deleted.');
            }} />
          )}
        </>
      )}
      </Body>
    </>
  );

  // The analysis from the confirm step lives as long as THIS import is open;
  // the job record only keeps the row count, and the mapping step needs the
  // headers. Stored against the job so it cannot reach another one.
  function setAnalysis(a: Analysis) { if (jobId) setAnalysedFor({ jobId, analysis: a }); }
}
/** The spreadsheet's columns as the server kept them, for a columns step reopened after a reload. */
function analysisFromJob(job: Job): Analysis | null {
  const u = job.upload;
  return u ? { headers: u.headers, rowCount: u.rowCount, suggestedMapping: u.suggestedMapping, sampleRows: [] } : null;
}

/** What checkImportCsv found (functions/src/import/csvCheck.ts). */
export interface LineFinding { line: number; column?: string; value?: string; message: string }
export interface CsvCheck {
  ok: boolean; fileProblems: string[]; lineErrors: LineFinding[]; lineErrorCount: number; warnings: LineFinding[]; warningCount: number;
  rowCount: number; headers: string[]; recognized: Record<string, string>; ignoredColumns: string[]; rowsChecked: boolean;
  /** What the importable lines bring with them: files named, text carried, lines with neither. */
  documents?: { filesNamed: number; linesWithFile: number; linesWithText: number; linesWithNeither: number };
}
/** How many findings of each kind the page lists; the count is always the full one and the download has them all. */
export const FINDINGS_SHOWN = 3;

export function FindingsTable({ caption, findings, total }: { caption: string; findings: LineFinding[]; total: number }) {
  const shown = findings.slice(0, FINDINGS_SHOWN);
  return (
    <div className={field.scroller} style={{ marginTop: 'var(--sp-3)' }}>
      <table className={field.table}>
        <caption>{caption}</caption>
        <thead><tr><th scope="col">Line</th><th scope="col">Column</th><th scope="col">Found</th><th scope="col">Problem</th></tr></thead>
        <tbody>
          {shown.map((f, i) => (
            <tr key={`${f.line}-${i}`}>
              <td className="tnum">{f.line}</td>
              <td>{f.column ?? <span style={{ color: 'var(--faint)' }}>whole line</span>}</td>
              <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{f.value ?? <span style={{ color: 'var(--faint)' }}>—</span>}</td>
              <td>{f.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {total > shown.length && <p className={form.hint}>Showing the first {shown.length} of {total.toLocaleString('en-US')}. The download lists all of them.</p>}
    </div>
  );
}

/**
 * Step 1, in two parts. The spreadsheet is uploaded and CHECKED first, and
 * the page says it is checking: is it a CSV at all, does line 1 name columns
 * we know, is every line sound. Only a spreadsheet that passes unlocks the
 * zip. One that does not is answered with the line, the column, what was
 * found and what is wrong, so it can be corrected and checked again; a
 * garbage file used to be "read" and only fail five hundred rows later.
 */
function UploadStep({ job, uploadUrls: initialUrls, onDone, onError }: { job: Job; uploadUrls: { csv: string; zip: string } | null; onDone: (a: Analysis) => void; onError: (m: string) => void }) {
  // The addresses from "Start an import" live only in that tab. A draft
  // opened again (a reload, the list of imports) asks for fresh ones, once.
  const urls = useRef(initialUrls);
  async function uploadUrlsNow(): Promise<{ csv: string; zip: string }> {
    if (!urls.current) {
      const r = await call<{ jobId: string }, { uploadUrls: { csv: string; zip: string } }>('getImportUploadUrls', { jobId: job.id });
      urls.current = r.uploadUrls;
    }
    return urls.current;
  }
  /** Upload, asking for a fresh address once if the one we hold has expired (they last an hour). */
  async function upload(kind: 'csv' | 'zip', file: File, contentType: string): Promise<void> {
    try {
      await putFile((await uploadUrlsNow())[kind], file, contentType);
    } catch (e) {
      if (!(e instanceof UploadWindowClosed)) throw e;
      urls.current = null;
      await putFile((await uploadUrlsNow())[kind], file, contentType);
    }
  }
  const [csv, setCsv] = useState<File | null>(null);
  const [zip, setZip] = useState<File | null>(null);
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'checking'>('idle');
  const [result, setResult] = useState<{ fileName: string; check: CsvCheck; findingsCsv: string | null } | null>(null);
  // "Import what can be imported": chosen with the findings in view.
  const [accept, setAccept] = useState(false);
  // How the documents come in: with a zip now, or the records alone. Asked, not assumed.
  const [docs, setDocs] = useState<'zip' | 'records'>('zip');
  const template = useQuery({ queryKey: ['import-template'], retry: false, queryFn: () => call<Record<string, never>, { template: string; headers: string[] }>('getImportTemplate', {}) });
  const csvProblem = tooLarge('csv', csv);
  const zipProblem = tooLarge('zip', zip);
  const [copied, setCopied] = useState(false);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (copiedTimer.current) clearTimeout(copiedTimer.current); }, []);
  function copyTemplate(text: string) {
    void navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      // "Copied" is an acknowledgement, not a state: back to "Copy CSV" so it can be pressed again.
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(false), COPIED_MS);
    }).catch(() => setCopied(false));
  }

  const checkCsv = useMutation({
    mutationFn: async () => {
      if (!csv) throw new Error('Choose the spreadsheet first.');
      setResult(null); setAccept(false); setDocs('zip');
      setPhase('uploading');
      await upload('csv', csv, 'text/csv');
      setPhase('checking');
      const r = await call<{ jobId: string; csvFileName: string }, { check: CsvCheck; findingsCsv: string | null }>('checkImportCsv', { jobId: job.id, csvFileName: csv.name }, LONG);
      return { fileName: csv.name, ...r };
    },
    onSuccess: (r) => setResult(r),
    onError: (e) => onError(errorText(e, 'The spreadsheet could not be checked.')),
    onSettled: () => setPhase('idle'),
  });

  const proceed = useMutation({
    mutationFn: async () => {
      if (!csv || !unlocked) throw new Error('Check the spreadsheet first.');
      const sending = withZip ? zip : null;
      if (sending) await upload('zip', sending, 'application/zip');
      return call<Record<string, unknown>, { csv: Analysis; zip?: { fileCount: number } }>('confirmImportUpload', {
        jobId: job.id, csvUploaded: true, zipUploaded: Boolean(sending),
        csvFileName: csv.name, csvSizeBytes: csv.size,
        ...(sending ? { zipFileName: sending.name, zipSizeBytes: sending.size } : {}),
        ...(accept ? { acceptFindings: true } : {}),
      }, LONG);
    },
    onSuccess: (r) => onDone(r.csv),
    onError: (e) => onError(errorText(e, 'The files could not be uploaded.')),
  });

  const check = result?.check ?? null;
  const unreadable = Boolean(check && check.fileProblems.length > 0);
  const errorsFound = check?.lineErrorCount ?? 0;
  const warningsFound = check?.warningCount ?? 0;
  const findings = errorsFound + warningsFound;
  const clean = Boolean(check && !unreadable && findings === 0);
  // The zip opens for a spreadsheet with nothing to report, or for one whose
  // findings the person has read and chosen to go on past. Never for a file
  // that cannot be read as a table.
  const unlocked = Boolean(check && !unreadable && (findings === 0 || accept));
  const passed = unlocked;
  const importable = check ? check.rowCount - errorsFound : 0;
  const d = check?.documents ?? null;
  const namesFiles = (d?.filesNamed ?? 0) > 0;
  // With nothing named there is nothing to choose; otherwise the person picks.
  const withZip = namesFiles ? docs === 'zip' : Boolean(zip);
  const n = (v: number) => v.toLocaleString('en-US');
  const busy = phase !== 'idle';
  const recognized = check ? Object.values(check.recognized) : [];

  return (
    <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle}>1. The spreadsheet, then its files</h2>
      <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
        A CSV with one policy per row{template.data ? `; the columns we understand are ${template.data.headers.join(', ')}` : ''}. Only the title is required.
        The spreadsheet is checked first. The zip, which holds the PDF or Word file each row names, is taken once it passes.
      </p>
      {template.data && (
        <div className={page.actions} style={{ marginBottom: 'var(--sp-3)' }}>
          <button type="button" className={page.btn} style={{ minWidth: 96 }} onClick={() => copyTemplate(template.data!.template)}>
            {copied ? 'Copied' : 'Copy CSV'}
          </button>
          <button type="button" className={page.btn} onClick={() => saveText(EXAMPLE_CSV_NAME, `${template.data!.template}\n`)}>
            Download CSV example
          </button>
          <span className="vh" role="status">{copied ? 'The example CSV was copied.' : ''}</span>
        </div>
      )}

      <div className={form.field} style={{ marginBottom: 'var(--sp-3)' }}>
        <label className={form.label} htmlFor="import-csv">Spreadsheet (CSV)</label>
        <p className={form.hint} id="import-csv-limits" style={{ margin: '0 0 var(--sp-2)' }}>
          Maximum {IMPORT_MAX.csvMb} MB and {IMPORT_MAX.rows.toLocaleString('en-US')} rows.
        </p>
        <input id="import-csv" type="file" accept=".csv,text/csv" disabled={busy || proceed.isPending} aria-describedby={csvProblem ? 'import-csv-limits import-csv-error' : 'import-csv-limits'} aria-invalid={csvProblem ? true : undefined}
          onChange={(e: ChangeEvent<HTMLInputElement>) => { setCsv(e.target.files?.[0] ?? null); setResult(null); setZip(null); }} />
        {csvProblem && <p className={form.error} id="import-csv-error" role="alert">{csvProblem}</p>}
      </div>
      <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }} disabled={!csv || Boolean(csvProblem) || busy || clean} onClick={() => checkCsv.mutate()}>
        {busy ? 'Checking…' : clean ? 'Spreadsheet checked' : result ? 'Check it again' : 'Check the spreadsheet'}
      </button>

      {/* What is happening, said while it happens. */}
      <div role="status" aria-live="polite" style={{ marginTop: 'var(--sp-3)' }}>
        {busy && csv && (
          <div className={`${page.notice} ${page.noticeInfo}`}>
            <strong>Checking {csv.name} before anything else is uploaded.</strong>
            <ol style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.3em', lineHeight: 1.7 }}>
              <li>{phase === 'uploading' ? 'Uploading the spreadsheet…' : 'Spreadsheet uploaded.'}</li>
              <li style={{ color: phase === 'checking' ? undefined : 'var(--muted)' }}>
                {phase === 'checking' ? 'Reading it: is it a CSV, does line 1 name columns we know, and is every line accurate and complete…' : 'Then it is read: the format, the column names, every line.'}
              </li>
            </ol>
            Nothing is imported at this step.
          </div>
        )}
        {check && clean && (
          <div className={`${page.notice} ${page.noticeInfo}`}>
            <strong>{result!.fileName} passed with nothing to report.</strong>{' '}
            {n(check.rowCount)} {check.rowCount === 1 ? 'policy' : 'policies'}; columns recognized: {recognized.join(', ')}.
            {check.ignoredColumns.length > 0 && <> Not used: {check.ignoredColumns.join(', ')}.</>}
            {!check.rowsChecked && <> No column was recognized as the title, so you will choose it at the next step and the lines are checked after that.</>}
          </div>
        )}
      </div>

      {check && unreadable && (
        <div role="alert" className={`${page.notice} ${page.noticeDanger}`} style={{ marginTop: 'var(--sp-1)' }}>
          <strong>{result!.fileName} cannot be imported.</strong> The file itself is the problem, so there is nothing to import from it:
          <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em' }}>
            {check.fileProblems.map((p) => <li key={p}>{p}</li>)}
          </ul>
          Correct the file, choose it again, and check it again.
        </div>
      )}

      {check && !unreadable && findings > 0 && (
        <div style={{ marginTop: 'var(--sp-1)' }}>
          <div role="alert" className={`${page.notice} ${errorsFound > 0 ? page.noticeDanger : page.noticeWarn}`}>
            <strong>
              {result!.fileName}: {n(findings)} {findings === 1 ? 'finding' : 'findings'} in {n(check.rowCount)} {check.rowCount === 1 ? 'line' : 'lines'}: {n(errorsFound)} {errorsFound === 1 ? 'error' : 'errors'} and {n(warningsFound)} {warningsFound === 1 ? 'warning' : 'warnings'}.
            </strong>
            <ul style={{ margin: 'var(--sp-2) 0 0', paddingLeft: '1.2em', lineHeight: 1.6 }}>
              <li><strong>An error</strong> means the line cannot be imported as it stands: no title, a title too long, the wrong number of values. If you go on, that line is <strong>left out</strong>. Everything else still imports.</li>
              <li><strong>A warning</strong> means the line <strong>is imported</strong>, but one value in it is not taken as written: an unknown status becomes {job.options.defaultStatus}, an owner that is not an email becomes you, a bad date is left blank, a file that is not PDF or Word is not attached. The policy may need correcting afterwards.</li>
            </ul>
          </div>
          {check.lineErrors.length > 0 && <FindingsTable caption={`Errors: ${n(errorsFound)} ${errorsFound === 1 ? 'line' : 'lines'} that would be left out`} findings={check.lineErrors} total={errorsFound} />}
          {check.warnings.length > 0 && <FindingsTable caption={`Warnings: ${n(warningsFound)} ${warningsFound === 1 ? 'value' : 'values'} that would not be taken as written`} findings={check.warnings} total={warningsFound} />}
        </div>
      )}
      {result?.findingsCsv && (
        <p style={{ marginTop: 'var(--sp-3)' }}>
          <button type="button" className={page.btn} onClick={() => saveText(`${result.fileName.replace(/\.csv$/i, '')}-problems.csv`, result.findingsCsv!)}>
            Download the full list{findings > 0 ? ` (${n(findings)})` : ''} as a CSV
          </button>
        </p>
      )}

      {check && !unreadable && findings > 0 && (
        <fieldset style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: 'var(--sp-4)', margin: 'var(--sp-4) 0 0' }}>
          <legend className={form.label} style={{ padding: '0 var(--sp-2)' }}>What would you like to do?</legend>
          <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)' }}>
            <strong>Correct the spreadsheet</strong> (recommended): fix the lines above, choose the file again, and check it again.
            Or go on with the file as it is:
          </p>
          <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', lineHeight: 1.5 }}>
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} style={{ marginTop: 3 }} />
            <span>
              <strong>Import what can be imported.</strong>{' '}
              {errorsFound > 0
                ? <>Leave out the {n(errorsFound)} {errorsFound === 1 ? 'line' : 'lines'} with errors and import the other {n(importable)}{warningsFound > 0 ? <>, {n(warningsFound)} of them with a value changed as the warnings say</> : null}.</>
                : <>Import all {n(importable)} lines, {n(warningsFound)} of them with a value changed as the warnings say.</>}
              {' '}The report made when the import finishes lists every line that was imported, every line that was not, and why.
            </span>
          </label>
        </fieldset>
      )}

      <section aria-labelledby="import-docs-title" style={{ margin: 'var(--sp-6) 0 0', opacity: passed ? 1 : 0.6 }}>
        <h3 className={page.sectionTitle} id="import-docs-title" style={{ fontSize: 15 }}>The documents</h3>

        {!passed && (
          <p className={page.itemMeta} style={{ maxWidth: '80ch' }}>
            <strong>{check && !unreadable && findings > 0 ? 'Available once you have chosen what to do about the findings above.' : 'Available once the spreadsheet has been checked.'}</strong>{' '}
            A policy library is its documents, and they come in a zip: the spreadsheet says which PDF or Word file belongs to which policy, and the zip holds the files.
            Once the spreadsheet is checked, this section tells you how many files it names and asks how you want them brought in.
          </p>
        )}

        {passed && d && (
          <p className={page.itemMeta} style={{ maxWidth: '80ch', marginBottom: 'var(--sp-3)' }}>
            {namesFiles
              ? <>Your spreadsheet names <strong>{n(d.filesNamed)} {d.filesNamed === 1 ? 'file' : 'files'}</strong> (PDF or Word) for {n(d.linesWithFile)} of its {n(importable)} {importable === 1 ? 'policy' : 'policies'}. The files themselves are not in the spreadsheet: they come in a zip, and each is attached to its policy by its name.</>
              : <>Your spreadsheet <strong>names no files</strong>, so there is no zip to send.</>}
            {d.linesWithText > 0 && <> {n(d.linesWithText)} {d.linesWithText === 1 ? 'policy carries its' : 'policies carry their'} text in the content column.</>}
            {d.linesWithNeither > 0 && <> <strong>{n(d.linesWithNeither)} {d.linesWithNeither === 1 ? 'policy has' : 'policies have'} neither a file nor text</strong>, and will be created as a record with nothing to read, whichever way you go.</>}
          </p>
        )}

        {passed && namesFiles && (
          <fieldset disabled={proceed.isPending} style={{ border: 0, padding: 0, margin: 0 }}>
            <legend className="vh">How should the documents come in?</legend>

            <label className={own.choice} data-on={docs === 'zip' || undefined}>
              <input type="radio" name="import-docs" checked={docs === 'zip'} onChange={() => setDocs('zip')} />
              <span>
                <strong>Upload the zip now, and import the policies with their documents.</strong> Recommended.
                <span className={own.choiceWhy}>
                  Pick this when you have the files. Each policy is created with its document already attached, so staff can read it the moment it is published.
                  Before anything is imported you will see exactly which files matched the spreadsheet and which did not, and can send a corrected zip.
                </span>
              </span>
            </label>
            {docs === 'zip' && (
              <div className={own.choiceBody}>
                <label className={form.label} htmlFor="import-zip">Files (zip)</label>
                <p className={form.hint} id="import-zip-limits" style={{ margin: '0 0 var(--sp-2)' }}>
                  Maximum zip size: {IMPORT_MAX.zipMb} MB, and no more than {IMPORT_MAX.unpackedMb / 1024} GB once unpacked.
                  <br />
                  Maximum size of each PDF or Word file inside it: {IMPORT_MAX.fileMb} MB. A larger file is left out and listed in the report; its policy is still created.
                  <br />
                  A file is matched by its name, whatever folder it is in and whatever its upper or lower case. A library over {IMPORT_MAX.zipMb} MB goes in as several imports, each with its own spreadsheet and zip.
                </p>
                <input id="import-zip" type="file" accept=".zip,application/zip" aria-describedby={zipProblem ? 'import-zip-limits import-zip-error' : 'import-zip-limits'} aria-invalid={zipProblem ? true : undefined} onChange={(e: ChangeEvent<HTMLInputElement>) => setZip(e.target.files?.[0] ?? null)} />
                {zipProblem && <p className={form.error} id="import-zip-error" role="alert">{zipProblem}</p>}
              </div>
            )}

            <label className={own.choice} data-on={docs === 'records' || undefined}>
              <input type="radio" name="import-docs" checked={docs === 'records'} onChange={() => setDocs('records')} />
              <span>
                <strong>Import the records only, and attach the documents later.</strong>
                <span className={own.choiceWhy}>
                  Pick this when the files are not ready, or when you want to see that the titles, numbers, departments, owners and review dates come in right before sending a large zip.
                  {' '}<strong>{n(d!.linesWithFile)} {d!.linesWithFile === 1 ? 'policy' : 'policies'} will be created with no document</strong>: a record staff can find but not read.
                  Each one needs its file attached afterwards from the policy's own page, one at a time (a published policy takes it as a new version), so this is slower for a whole library.
                  You can still change your mind: the zip can be added at the “What will happen” step, before anything is imported.
                </span>
              </span>
            </label>
          </fieldset>
        )}

        {passed && (
          <div style={{ marginTop: 'var(--sp-4)' }}>
            <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }}
              disabled={proceed.isPending || (withZip && (!zip || Boolean(zipProblem)))} onClick={() => proceed.mutate()}>
              {proceed.isPending ? (withZip ? 'Uploading the zip…' : 'Continuing…') : !namesFiles ? 'Continue to the columns' : withZip ? 'Upload the zip and continue' : 'Continue with the records only'}
            </button>
            {withZip && !zip && <span className={page.itemMeta} style={{ marginLeft: 'var(--sp-3)' }}>Choose the zip first.</span>}
          </div>
        )}
      </section>
    </section>
  );
}

/**
 * The spreadsheet's files against the zip's, stated in full before the run.
 * A row whose file is missing still becomes a policy, with no document: fine
 * for a few stragglers, a disaster for the wrong zip. So the page says how
 * many, says what that means, shows three with the file that was probably
 * meant, and asks: a corrected zip, or go on without them.
 */
function FilesMatchPanel({ job, validation, accept, onAccept, downloading, onDownload, onChecked, onError }: {
  job: Job; validation: Validation; accept: boolean; onAccept: (v: boolean) => void; downloading: boolean; onDownload: () => void; onChecked: () => void; onError: (m: string) => void;
}) {
  const f = validation.files;
  const [zip, setZip] = useState<File | null>(null);
  const zipProblem = tooLarge('zip', zip);
  const replace = useMutation({
    mutationFn: async () => {
      if (!zip) throw new Error('Choose the zip first.');
      const { uploadUrls } = await call<{ jobId: string }, { uploadUrls: { csv: string; zip: string } }>('getImportUploadUrls', { jobId: job.id });
      await putFile(uploadUrls.zip, zip, 'application/zip');
      await call<{ jobId: string; zipFileName: string; zipSizeBytes: number }, unknown>('replaceImportZip', { jobId: job.id, zipFileName: zip.name, zipSizeBytes: zip.size }, LONG);
      return call<{ jobId: string }, unknown>('validateImportJob', { jobId: job.id }, LONG);
    },
    onSuccess: () => { setZip(null); onChecked(); },
    onError: (e) => onError(errorText(e, 'The zip could not be checked.')),
  });
  if (!f || (f.named === 0 && !f.zipProvided)) return null;
  const n = (v: number) => v.toLocaleString('en-US');
  const files = (v: number) => `${n(v)} ${v === 1 ? 'file' : 'files'}`;
  const allThere = f.missingCount === 0;

  return (
    <section aria-labelledby="import-files-title" style={{ marginTop: 'var(--sp-5)' }}>
      <h3 className={page.sectionTitle} id="import-files-title" style={{ fontSize: 15 }}>The files</h3>
      <div role={allThere ? 'status' : 'alert'} className={`${page.notice} ${allThere ? page.noticeInfo : f.looksLikeWrongZip || !f.zipProvided ? page.noticeDanger : page.noticeWarn}`}>
        <strong>
          {f.named === 0
            ? 'The spreadsheet names no files, so none will be attached.'
            : allThere
              ? `All ${files(f.named)} the spreadsheet names ${f.named === 1 ? 'is' : 'are'} in the zip.`
              : !f.zipProvided
                ? `The spreadsheet names ${files(f.named)} and no zip was uploaded.`
                : `${n(f.found)} of the ${files(f.named)} the spreadsheet names ${f.found === 1 ? 'was' : 'were'} found in the zip. ${n(f.missingCount)} ${f.missingCount === 1 ? 'is' : 'are'} missing.`}
        </strong>
        {f.looksLikeWrongZip && <> <strong>This looks like the wrong zip</strong>, or the files are named differently from what the spreadsheet says.</>}
        {!allThere && (
          <p style={{ margin: 'var(--sp-2) 0 0' }}>
            A policy whose file is missing <strong>is still created, with no document attached</strong>. Staff would open it and find nothing to read until someone attaches the file by hand.
            {f.nearMatchCount > 0 && <> {n(f.nearMatchCount)} of the missing {f.nearMatchCount === 1 ? 'file has' : 'files have'} a near match in the zip (the same name apart from spacing, punctuation or the extension), which points at how the files were named rather than at files that are absent. Names must match exactly, apart from upper and lower case and folders.</>}
          </p>
        )}
        {(f.unreferencedCount > 0 || f.wrongTypeCount > 0) && (
          <p style={{ margin: 'var(--sp-2) 0 0' }}>
            {f.unreferencedCount > 0 && <>{files(f.unreferencedCount)} in the zip {f.unreferencedCount === 1 ? 'is' : 'are'} named by no row and will be ignored{f.unreferenced.length ? ` (${f.unreferenced.slice(0, FINDINGS_SHOWN).join(', ')}${f.unreferencedCount > FINDINGS_SHOWN ? ', ...' : ''})` : ''}. </>}
            {f.wrongTypeCount > 0 && <>{files(f.wrongTypeCount)} {f.wrongTypeCount === 1 ? 'is' : 'are'} not PDF or Word and cannot be imported{f.wrongType.length ? ` (${f.wrongType.slice(0, FINDINGS_SHOWN).join(', ')}${f.wrongTypeCount > FINDINGS_SHOWN ? ', ...' : ''})` : ''}.</>}
          </p>
        )}
      </div>

      {!allThere && (
        <>
          <div className={field.scroller} style={{ marginTop: 'var(--sp-3)' }}>
            <table className={field.table}>
              <caption>Missing: {files(f.missingCount)} the spreadsheet names and the zip does not have</caption>
              <thead><tr><th scope="col">Line</th><th scope="col">Policy</th><th scope="col">File the spreadsheet names</th><th scope="col">Closest file in the zip</th></tr></thead>
              <tbody>
                {f.missing.slice(0, FINDINGS_SHOWN).map((m) => (
                  <tr key={`${m.line}-${m.fileName}`}>
                    <td className="tnum">{m.line}</td>
                    <td>{m.title}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{m.fileName}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5 }}>{m.closest ?? <span style={{ color: 'var(--faint)', fontFamily: 'var(--font)' }}>nothing like it</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {f.missingCount > FINDINGS_SHOWN && <p className={form.hint}>Showing the first {FINDINGS_SHOWN} of {n(f.missingCount)}. The download lists all of them.</p>}
          </div>
          <p style={{ marginTop: 'var(--sp-3)' }}>
            <button type="button" className={page.btn} disabled={downloading} onClick={onDownload}>Download the full list ({n(f.missingCount)}) as a CSV</button>
          </p>

          <fieldset style={{ border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)', padding: 'var(--sp-4)', margin: 'var(--sp-4) 0 0' }}>
            <legend className={form.label} style={{ padding: '0 var(--sp-2)' }}>What would you like to do about the files?</legend>
            <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-2)' }}>
              <strong>Upload a corrected zip</strong> (recommended). It replaces the one you sent and everything is checked again; the spreadsheet stays as it is.
            </p>
            <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="vh" htmlFor="import-zip-again">Corrected zip</label>
              <input id="import-zip-again" type="file" accept=".zip,application/zip" disabled={replace.isPending} aria-describedby="import-zip-again-limits" onChange={(e: ChangeEvent<HTMLInputElement>) => setZip(e.target.files?.[0] ?? null)} />
              <button type="button" className={page.btn} disabled={!zip || Boolean(zipProblem) || replace.isPending} onClick={() => replace.mutate()}>
                {replace.isPending ? 'Uploading and checking again…' : 'Upload this zip and check again'}
              </button>
            </div>
            <p className={form.hint} id="import-zip-again-limits">Maximum {IMPORT_MAX.zipMb} MB; each file inside up to {IMPORT_MAX.fileMb} MB.</p>
            {zipProblem && <p className={form.error} role="alert">{zipProblem}</p>}
            <p className={page.itemMeta} style={{ margin: 'var(--sp-3) 0 var(--sp-2)' }}>Or go on with what is there:</p>
            <label style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'flex-start', lineHeight: 1.5 }}>
              <input type="checkbox" checked={accept} disabled={replace.isPending} onChange={(e) => onAccept(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                <strong>Import what can be imported.</strong>{' '}
                {f.found > 0
                  ? <>Attach the {files(f.found)} that {f.found === 1 ? 'is' : 'are'} there. The policies that name the other {n(f.missingCount)} are created without {f.missingCount === 1 ? 'it' : 'them'}.</>
                  : <>Create every policy with no document attached.</>}
                {' '}The report made when the import finishes lists each file that was not attached, and why.
              </span>
            </label>
          </fieldset>
        </>
      )}
    </section>
  );
}

function MapStep({ job, analysis, onSaved, onError, onRun }: { job: Job; analysis: Analysis | null; onSaved: () => void; onError: (m: string) => void; onRun: () => void }) {
  const headers = analysis?.headers ?? [];
  // What was saved wins over what we guessed, but only where something WAS
  // saved: a new job's mapping is `{ title: '' }`, and letting that blank
  // overwrite the guess left Title unmapped and the button below disabled
  // for a spreadsheet whose column is literally called "title".
  const initial = useMemo(() => {
    const saved = Object.fromEntries(Object.entries(job.mapping ?? {}).filter(([, v]) => typeof v === 'string' && v));
    return { ...(analysis?.suggestedMapping ?? {}), ...saved };
  }, [analysis, job.mapping]);
  const [mapping, setMapping] = useState<Record<string, string>>(() => Object.fromEntries(Object.entries(initial).filter(([, v]) => typeof v === 'string' && v) as [string, string][]));
  const [options, setOptions] = useState<Options>(job.options);
  const validation = job.validation;
  /** No column for it and no default chosen: the server would refuse the save. */
  const needsType = !mapping.documentType && !options.defaultDocumentTypeId;
  const save = useMutation({
    mutationFn: async () => {
      const clean = Object.fromEntries(Object.entries(mapping).filter(([, v]) => v));
      await call<Record<string, unknown>, unknown>('saveImportMapping', { jobId: job.id, mapping: clean, options });
      return call<{ jobId: string }, { validation: Validation }>('validateImportJob', { jobId: job.id }, LONG);
    },
    onSuccess: onSaved,
    onError: (e) => onError(errorText(e, 'The mapping could not be saved.')),
  });
  // Policies created without their document is a choice made with the list in view.
  const [acceptFiles, setAcceptFiles] = useState(false);
  const run = useMutation({
    mutationFn: () => call<{ jobId: string; acceptMissingFiles?: boolean }, unknown>('runImportJob', { jobId: job.id, ...(acceptFiles ? { acceptMissingFiles: true } : {}) }, LONG),
    onSuccess: onRun,
    onError: (e) => onError(errorText(e, 'The import could not start.')),
  });
  const download = useMutation({
    mutationFn: (which: 'errors' | 'missing') => call<{ jobId: string }, { downloadUrl?: string; url?: string }>(which === 'errors' ? 'downloadErrorsCsv' : 'downloadMissingFilesCsv', { jobId: job.id }),
    onSuccess: (r) => { const u = r.downloadUrl ?? r.url; if (u) window.open(u, '_blank', 'noopener'); },
    onError: (e) => onError(errorText(e, 'The file could not be prepared.')),
  });

  return (
    <>
      <section className={own.panel} style={{ padding: 'var(--sp-5)', marginBottom: 'var(--sp-5)' }}>
        <h2 className={page.sectionTitle}>2. Which column is which</h2>
        <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
          {job.csvFile.fileName}, {job.execution.totalRows} rows{job.zipFile ? `, with ${job.zipFile.fileName}` : ', no zip'}.
          {headers.length === 0 && ' The column names are not on hand (the page was reloaded); the saved mapping is shown as text.'}
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 'var(--sp-3)' }}>
          {MAPPING_FIELDS.map((f) => (
            <div key={f.key} className={form.field}>
              <label className={form.label} htmlFor={`map-${f.key}`}>{f.label}{f.required && ' (required)'}</label>
              {headers.length ? (
                <select id={`map-${f.key}`} className={form.select} value={mapping[f.key] ?? ''} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}>
                  <option value="">Not in the sheet</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              ) : (
                <input id={`map-${f.key}`} className={form.input} maxLength={ID_MAX} value={mapping[f.key] ?? ''} onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })} />
              )}
              {f.hint && <p className={form.hint}>{f.hint}</p>}
            </div>
          ))}
        </div>
        <h3 style={{ fontSize: 'var(--fs-md)', margin: 'var(--sp-4) 0 var(--sp-2)' }}>Options</h3>
        <label className={field.roleToggle} style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
          <input type="checkbox" checked={options.createTaxonomyIfMissing} onChange={(e) => setOptions({ ...options, createTaxonomyIfMissing: e.target.checked })} />
          <span>Create departments and categories the sheet names that do not exist yet</span>
        </label>
        <label className={field.roleToggle} style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
          <input type="checkbox" checked={options.generatePolicyNumbers} onChange={(e) => setOptions({ ...options, generatePolicyNumbers: e.target.checked })} />
          <span>Generate a policy number where the sheet has none</span>
        </label>
        <label className={field.roleToggle} style={{ display: 'block', marginBottom: 'var(--sp-2)' }}>
          <input type="checkbox" checked={options.skipDuplicateLegacyIds} onChange={(e) => setOptions({ ...options, skipDuplicateLegacyIds: e.target.checked })} />
          <span>Skip rows whose legacy id is already on record</span>
        </label>
        {/*
          * EVERY POLICY SAYS WHAT KIND OF DOCUMENT IT IS. A sheet with no type
          * column needs this; one with a column may still have blank lines.
          */}
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <label className={form.label} htmlFor="opt-doctype">
            Document type when {mapping.documentType ? 'a line has none' : 'the sheet has none'}
            {!mapping.documentType && ' (required)'}
          </label>
          <select id="opt-doctype" className={form.select} style={{ maxWidth: 420 }} aria-describedby="opt-doctype-help"
            aria-invalid={needsType || undefined}
            value={options.defaultDocumentTypeId ?? ''}
            onChange={(e) => setOptions({ ...options, defaultDocumentTypeId: e.target.value || null })}>
            <option value="">{mapping.documentType ? 'None: a line without a type is left out' : 'Choose a type'}</option>
            {(job.documentTypes ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <p className={needsType ? form.error : form.hint} id="opt-doctype-help">
            {(job.documentTypes ?? []).length === 0
              ? 'This organization has no document types yet. Add them under Taxonomy, then come back.'
              : needsType
                ? 'Every policy needs a document type. Choose a Document type column above, or the type to use here.'
                : 'Every policy needs a document type. A line that names its own keeps it.'}
          </p>
        </div>
        <div style={{ marginTop: 'var(--sp-3)' }}>
          <label className={form.label} htmlFor="opt-status">Status when the sheet has none</label>
          <select id="opt-status" className={form.select} style={{ maxWidth: 420 }} aria-describedby="opt-status-help" value={options.defaultStatus} onChange={(e) => setOptions({ ...options, defaultStatus: e.target.value as Options['defaultStatus'] })}>
            <option value="draft">Draft, to be reviewed and published here</option>
            <option value="published">Published, as they stand</option>
          </select>
          {/* Both options explained, always, with the chosen one marked: the
              choice decides whether staff can read the library tonight. */}
          <div id="opt-status-help" className={own.optionHelp}>
            <p className={form.hint} style={{ margin: '0 0 var(--sp-2)' }}>
              {mapping.status
                ? <>A row that says <strong>draft</strong> or <strong>published</strong> in its <code>{mapping.status}</code> column keeps what it says. This choice applies only to rows that say nothing, or something else.</>
                : <>Your spreadsheet has no status column, so this applies to <strong>every</strong> policy in it.</>}
            </p>
            <dl>
              <div data-on={options.defaultStatus === 'draft' || undefined}>
                <dt>Draft, to be reviewed and published here{options.defaultStatus === 'draft' && <span className={own.optionChosen}>Chosen</span>}</dt>
                <dd>
                  Staff do not see these policies yet. An author opens each one, checks its document and where it is filed, and sends it for approval; the approval publishes it and asks the right people to acknowledge it.
                  <strong> Choose this</strong> when the library has not been reviewed lately, or you want a person to check each policy before anyone relies on it. It is the safe choice, and the slow one for a large library.
                </dd>
              </div>
              <div data-on={options.defaultStatus === 'published' || undefined}>
                <dt>Published, as they stand{options.defaultStatus === 'published' && <span className={own.optionChosen}>Chosen</span>}</dt>
                <dd>
                  Staff can read these policies the moment the import finishes, exactly as they are in the spreadsheet. Nobody approves them here first, and the import asks nobody to acknowledge them.
                  <strong> Choose this</strong> when you are moving a library that is already approved and in force, and the goal is for staff to find it here tomorrow.
                </dd>
              </div>
            </dl>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 'var(--sp-4)', flexWrap: 'wrap', marginTop: 'var(--sp-4)' }}>
          <div className={form.field}>
            <label className={form.label} htmlFor="opt-review">Review cycle when the sheet has no review date (months)</label>
            <input id="opt-review" type="number" min={1} max={60} maxLength={2} className={form.input} style={{ maxWidth: 120 }} value={options.defaultReviewCycleMonths} onChange={(e) => setOptions({ ...options, defaultReviewCycleMonths: Number(e.target.value) || 12 })} />
          </div>
        </div>
        <div style={{ marginTop: 'var(--sp-4)' }}>
          <button type="button" className={own.googleBtn} style={{ width: 'auto', padding: '9px 16px' }} disabled={!mapping.title || needsType || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Checking…' : 'Save and check the sheet'}
          </button>
        </div>
      </section>

      {validation && job.status === 'validated' && (
        <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
          <h2 className={page.sectionTitle}>3. What will happen</h2>
          <dl className={page.facts}>
            <div><dt className={page.factTerm}>Rows</dt><dd className={`${page.factValue} tnum`}>{validation.totalRows}</dd></div>
            <div><dt className={page.factTerm}>Will be created</dt><dd className={`${page.factValue} tnum`}>{validation.validRows}</dd></div>
            <div><dt className={page.factTerm}>Errors</dt><dd className={`${page.factValue} tnum`}>{validation.errorCount}</dd></div>
            <div><dt className={page.factTerm}>Warnings</dt><dd className={`${page.factValue} tnum`}>{validation.warningCount}</dd></div>
            <div><dt className={page.factTerm}>Duplicate legacy ids</dt><dd className={`${page.factValue} tnum`}>{validation.duplicateLegacyIds.length}</dd></div>
          </dl>
          {validation.errorCount > 0 && (
            <div style={{ marginTop: 'var(--sp-3)' }}>
              <button type="button" className={page.btn} disabled={download.isPending} onClick={() => download.mutate('errors')}>Download the errors</button>
            </div>
          )}
          <FilesMatchPanel job={job} validation={validation} accept={acceptFiles} onAccept={setAcceptFiles} downloading={download.isPending} onDownload={() => download.mutate('missing')} onChecked={() => { setAcceptFiles(false); onSaved(); }} onError={onError} />
          <ImportOwners jobId={job.id} named={validation.owners?.people ?? 0} ran={false} />
          <p className={page.itemMeta} style={{ margin: 'var(--sp-3) 0' }}>
            {validation.canProceed
              ? 'Rows with errors are skipped; the rest are created. Fix the sheet and check again if you would rather have all of them.'
              : 'The sheet cannot be imported as it is. Fix the errors and check again.'}
          </p>
          {validation.aboveSupportedSize && (
            <p className={page.itemMeta} style={{ maxWidth: '80ch' }} role="status">
              {/* Honest boundary, not an arbitrary one: the run resumes if it is
                  interrupted and imports nothing twice, but the browser is what
                  asks for each batch, so it is not an unattended job. */}
              <strong>This is a large import.</strong> {validation.aboveSupportedSize.rows.toLocaleString('en-US')} rows is above the{' '}
              {validation.aboveSupportedSize.supported.toLocaleString('en-US')} StandingDocs has run end to end. It will work, and if it is
              interrupted you can reopen this import and it carries on from where it stopped without importing anything twice.
              What it is not is something to start and walk away from: this page asks for each batch of{' '}
              {String(50)} as the last one finishes, so leave the tab open until it says Done.
            </p>
          )}
          <button type="button" className={`${own.googleBtn} ${own.primaryWhenReady}`} style={{ width: 'auto', padding: '9px 16px' }} disabled={!validation.canProceed || run.isPending || ((validation.files?.missingCount ?? 0) > 0 && !acceptFiles)} onClick={() => run.mutate()}>
            {run.isPending ? 'Starting…' : `Import ${validation.validRows} ${validation.validRows === 1 ? 'policy' : 'policies'}`}
          </button>
        </section>
      )}
    </>
  );
}

function RunStep({ job, onTick, onError }: { job: Job; onTick: () => void; onError: (m: string) => void }) {
  // The engine works in batches and asks to be called again until done;
  // this drives it, one continuation per tick, while the status query
  // refreshes the numbers.
  const busy = useRef(false);
  useEffect(() => {
    if (busy.current) return;
    busy.current = true;
    call<{ jobId: string }, { completed?: boolean }>('continueImportJob', { jobId: job.id }, LONG)
      .then(() => onTick())
      .catch((e) => onError(errorText(e, 'The import stopped.')))
      .finally(() => { busy.current = false; });
  }, [job.execution.cursor, job.id, onTick, onError]);
  const pct = job.execution.progress;
  return (
    <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle}>4. Importing</h2>
      <p className={page.itemMeta} role="status" aria-live="polite">{job.execution.cursor} of {job.execution.totalRows} rows · {job.stats.created} created, {job.stats.failed} failed, {(job.stats.alreadyImported ?? 0)} already imported</p>
      <div aria-hidden="true" style={{ height: 8, background: 'var(--line)', borderRadius: 4, overflow: 'hidden', marginTop: 'var(--sp-2)' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--accent)' }} />
      </div>
    </section>
  );
}

/** The word that has to be typed. */
export const DELETE_WORD = 'delete';

/**
 * Delete the RECORD of an import, never the library. Offered only inside an
 * opened import, so nobody deletes one they have not looked at, and only
 * after typing the word: a click is too easy for something that cannot be
 * undone. It says exactly what goes and what stays, because "delete" beside
 * "5,000 policies imported" reads as something far worse than it is.
 */
function DeleteImportRecord({ job, onDeleted, onError }: { job: Job; onDeleted: (policiesKept: number) => void; onError: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [word, setWord] = useState('');
  const id = useId();
  const remove = useMutation({
    mutationFn: () => call<{ jobId: string; confirm: string }, { policiesKept: number }>('deleteImportJob', { jobId: job.id, confirm: word.trim().toLowerCase() }, LONG),
    onSuccess: (r) => onDeleted(r.policiesKept ?? 0),
    onError: (e) => onError(errorText(e, 'The record could not be deleted.')),
  });
  const imported = job.stats?.created ?? 0;
  const typed = word.trim().toLowerCase() === DELETE_WORD;

  return (
    <section className={`${own.panel} ${own.dangerPanel}`} aria-labelledby={`${id}-title`} style={{ padding: 'var(--sp-5)', marginTop: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle} id={`${id}-title`}>Delete the record of this import</h2>
      <p className={page.itemMeta} style={{ maxWidth: '80ch', margin: '0 0 var(--sp-3)' }}>
        This removes the record only: the import's entry under Previous imports, its rows and findings, the spreadsheet and zip you uploaded, and its report.
        {imported > 0
          ? <> <strong>The {imported.toLocaleString('en-US')} {imported === 1 ? 'policy it imported stays' : 'policies it imported stay'} in the library, with {imported === 1 ? 'its document' : 'their documents'}.</strong></>
          : <> <strong>It imported no policies, so nothing in the library is affected.</strong></>}
        {imported > 0 && job.status === 'completed' && <> After this the import can no longer be rolled back, because a rollback works from this record. Download the report first if you want to keep it.</>}
      </p>
      {imported > 0 && (
        <p className={page.itemMeta} style={{ maxWidth: '80ch', margin: '0 0 var(--sp-3)' }}>
          {/* Each imported policy carries its own copy of where it came from
              (importSource), so deleting this record leaves every one of them
              still able to answer the question a surveyor asks. It used to be
              the only place the spreadsheet's name was written down. */}
          Each imported policy keeps its own record of where it came from, under
          &ldquo;Where this came from&rdquo; on the policy: the spreadsheet&rsquo;s name and checksum, the line it
          was on, who imported it and when. That survives this deletion. What goes is the
          operational record of the run: the uploaded files, the per-line findings and the
          ability to roll back.
        </p>
      )}
      {!open ? (
        <button type="button" className={`${page.btn} ${own.dangerBtn}`} onClick={() => { setWord(''); setOpen(true); }}>Delete this record…</button>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); if (typed) remove.mutate(); }} style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label className={form.label} htmlFor={`${id}-word`}>Type <strong>delete</strong> to confirm</label>
            <input id={`${id}-word`} type="text" className={form.input} style={{ width: 200 }} maxLength={CODE_MAX} value={word} onChange={(e) => setWord(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} disabled={remove.isPending} autoFocus />
          </div>
          <button type="submit" className={page.btn} style={typed ? { borderColor: 'var(--danger-ink)', color: 'var(--danger-ink)' } : undefined} disabled={!typed || remove.isPending}>
            {remove.isPending ? 'Deleting…' : 'Delete the record'}
          </button>
          <button type="button" className={page.btn} disabled={remove.isPending} onClick={() => setOpen(false)}>Keep it</button>
        </form>
      )}
    </section>
  );
}

function DoneStep({ job, errorSample, onChanged, onError, onNotice }: { job: Job; errorSample: Array<{ rowNumber: number; message?: string; error?: string }>; onChanged: () => void; onError: (m: string) => void; onNotice: (m: string) => void }) {
  const [arming, setArming] = useState(false);
  const rollback = useMutation({
    mutationFn: () => call<{ jobId: string; confirmJobId: string }, unknown>('rollbackImportJob', { jobId: job.id, confirmJobId: job.id }, LONG),
    onSuccess: () => { onNotice('Rolled back. The policies this import created are gone.'); onChanged(); },
    onError: (e) => onError(errorText(e, 'The rollback failed.')),
  });
  // One call builds both files; whichever button was pressed is saved, the
  // other kept for the next press without another round trip.
  const report = useMutation({
    mutationFn: () => call<{ jobId: string }, { xlsx: ReportFile; pdf: ReportFile }>('exportImportReport', { jobId: job.id }, LONG),
    onError: (e) => onError(errorText(e, 'The report could not be built.')),
  });
  async function download(which: 'xlsx' | 'pdf') {
    const r = report.data ?? await report.mutateAsync().catch(() => null);
    if (!r) return;
    saveFile(r[which], which === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' : 'application/pdf');
  }
  return (
    <section className={own.panel} style={{ padding: 'var(--sp-5)' }}>
      <h2 className={page.sectionTitle}>{job.status === 'completed' ? 'Done' : job.status === 'rolled_back' ? 'Rolled back' : 'Failed'}</h2>
      {/* A finished rollback is not an error. Older jobs still carry the
          "Rolling back..." written when theirs began; it is never shown. */}
      {job.status === 'rolled_back'
        ? <p className={page.itemMeta} role="status" style={{ maxWidth: '78ch' }}><strong>The rollback finished.</strong> Every policy this import created has been removed, with its files, and the same spreadsheet can be imported again.</p>
        : job.error && <div className={own.error} role="alert">{job.error}</div>}
      <dl className={page.facts}>
        <div><dt className={page.factTerm}>Created</dt><dd className={`${page.factValue} tnum`}>{job.stats.created}</dd></div>
        <div><dt className={page.factTerm}>Failed</dt><dd className={`${page.factValue} tnum`}>{job.stats.failed}</dd></div>
        <div><dt className={page.factTerm}>Already imported</dt><dd className={`${page.factValue} tnum`}>{job.stats.alreadyImported ?? 0}</dd></div>
        <div><dt className={page.factTerm}>Held back with their version group</dt><dd className={`${page.factValue} tnum`}>{job.stats.blocked ?? 0}</dd></div>
        {job.stats.skipped > 0 && <div><dt className={page.factTerm}>Skipped</dt><dd className={`${page.factValue} tnum`}>{job.stats.skipped}</dd></div>}
        <div><dt className={page.factTerm}>Files stored</dt><dd className={`${page.factValue} tnum`}>{job.stats.filesStored ?? 0}</dd></div>
        <div><dt className={page.factTerm}>Files missing</dt><dd className={`${page.factValue} tnum`}>{job.stats.filesMissing}</dd></div>
        <div><dt className={page.factTerm}>Files refused</dt><dd className={`${page.factValue} tnum`}>{job.stats.filesRejected ?? 0}</dd></div>
      </dl>
      {job.status !== 'rolled_back' && (job.stats.withNotes ?? 0) > 0 && (
        <p className={page.itemMeta} style={{ maxWidth: '78ch' }} role="status">
          {job.stats.withNotes} imported {job.stats.withNotes === 1 ? 'policy carries' : 'policies carry'} a note: the sheet said something that could not be used, such as an owner who is not a member yet. The policy was created; the report names the line and what was not used.
        </p>
      )}
      {job.status !== 'rolled_back' && (job.stats.ackPending ?? 0) > 0 && (
        <p className={page.itemMeta} style={{ maxWidth: '78ch' }} role="status">
          {job.stats.ackPending} published {job.stats.ackPending === 1 ? 'policy is' : 'policies are'} still being sent to the people who must read {job.stats.ackPending === 1 ? 'it' : 'them'}. StandingDocs finishes this by itself within the hour; nothing was lost and nothing needs doing here.
        </p>
      )}
      {job.status === 'completed' && <ImportOwners jobId={job.id} named={job.validation?.owners?.people ?? 0} ran />}
      <div style={{ marginTop: 'var(--sp-5)' }}>
        <h3 className={page.sectionTitle} style={{ fontSize: 15 }}>After-action report</h3>
        <p className={page.itemMeta} style={{ margin: '0 0 var(--sp-3)', maxWidth: '78ch' }}>
          {job.report
            ? <>Created when the import finished{job.report.generatedAt ? ` (${new Date(job.report.generatedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })})` : ''} and kept with the import. </>
            : <>Built when you ask for it and kept with the import. </>}
          {job.report?.imported != null && job.report?.notImported != null && (
            <><strong>{job.report.imported.toLocaleString('en-US')} imported, {job.report.notImported.toLocaleString('en-US')} not imported.</strong>{' '}</>
          )}
          The workbook opens with what was imported and what was not, line by line with the reason, followed by every row, problem and file for whoever corrects the spreadsheet. The PDF is the one-page account for whoever asked for the import.
          {job.csvCheck?.acceptedFindings && <> This import went ahead past {job.csvCheck.lineErrors.toLocaleString('en-US')} {job.csvCheck.lineErrors === 1 ? 'line' : 'lines'} with errors, which were left out by choice; they are listed under “Not imported”.</>}
        </p>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={report.isPending} onClick={() => void download('xlsx')}>{report.isPending ? 'Building the report…' : 'Download the report (Excel)'}</button>
          <button type="button" className={page.btn} disabled={report.isPending} onClick={() => void download('pdf')}>Download the summary (PDF)</button>
        </div>
      </div>
      {errorSample.length > 0 && (
        <ul className={page.list} aria-label="Rows that failed" style={{ marginTop: 'var(--sp-3)' }}>
          {errorSample.map((e) => <li key={e.rowNumber} className={page.listItem}><div className={page.itemMeta}>Row {e.rowNumber}: {e.message ?? e.error ?? 'failed'}</div></li>)}
        </ul>
      )}
      {job.status === 'completed' && (
        <div style={{ marginTop: 'var(--sp-4)', display: 'flex', gap: 'var(--sp-3)', alignItems: 'center', flexWrap: 'wrap' }}>
          {!arming ? (
            <button type="button" className={page.btn} onClick={() => setArming(true)}>Roll this import back</button>
          ) : (
            <>
              <span className={page.itemMeta}>Removes every policy this import created. Sure?</span>
              <button type="button" className={`${page.btn} ${page.btnPrimary}`} disabled={rollback.isPending} onClick={() => rollback.mutate()}>{rollback.isPending ? 'Rolling back…' : 'Yes, roll it back'}</button>
              <button type="button" className={page.btn} onClick={() => setArming(false)}>Keep it</button>
            </>
          )}
        </div>
      )}
    </section>
  );
}
